import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Flame, Heart, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired | timeout
  const [record, setRecord] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setRecord(res.data);
        const ps = res.data.payment_status;
        if (ps === "paid") {
          setStatus("paid");
          return;
        }
        if (ps === "failed" || ps === "expired") {
          setStatus(ps);
          return;
        }
      } catch {
        /* transient */
      }
      if (attemptsRef.current >= 10) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div
        data-testid="payment-success-page"
        className="w-full max-w-lg rounded-2xl bg-[#5C0E1D] gold-border-strong p-8 sm:p-10 text-center fade-up"
      >
        {status === "polling" && (
          <>
            <Flame
              className="w-10 h-10 text-[#E7B04A] flicker mx-auto"
              strokeWidth={1.4}
              fill="rgba(231,176,74,0.15)"
            />
            <h1 className="mt-4 font-serif-display text-3xl text-[#FFFFFF]">
              Confirming your gift…
            </h1>
            <p className="mt-2 text-sm text-[#FBEBD3] italic font-scripture">
              A moment of silence as the scroll is signed.
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <Heart
              className="w-10 h-10 text-[#E7B04A] mx-auto"
              strokeWidth={1.5}
              fill="rgba(0,0,0,0.15)"
            />
            <h1 className="mt-4 font-serif-display text-3xl text-[#FFFFFF]" data-testid="payment-success-heading">
              Thank you.
            </h1>
            <p className="mt-3 text-[#FFFFFF] font-scripture">
              Your gift of{" "}
              <strong>
                ${((record?.amount || 0) / 100).toFixed(2)} {(record?.currency || "usd").toUpperCase()}
              </strong>{" "}
              keeps the candle lit for every seeker who arrives here.
            </p>
            <p className="mt-4 text-[0.72rem] text-[#FBEBD3] italic font-scripture">
              &ldquo;Each of you should give what you have decided in your heart to give,
              for God loves a cheerful giver.&rdquo; — 2 Corinthians 9:7
            </p>
            <Link
              to="/"
              data-testid="return-home-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E7B04A] px-5 py-2.5 text-sm text-[#5C0E1D] hover:bg-[#F5C76B] transition-colors"
            >
              Return to the sanctuary
            </Link>
          </>
        )}

        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="w-10 h-10 text-[#E7B04A] mx-auto" strokeWidth={1.5} />
            <h1 className="mt-4 font-serif-display text-3xl text-[#FFFFFF]">
              Something interrupted the gift.
            </h1>
            <p className="mt-3 text-sm text-[#FBEBD3] font-scripture italic">
              No charges were made. You are welcome to try again — or simply keep asking.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-full gold-border bg-[#5C0E1D] px-5 py-2.5 text-sm text-[#FFFFFF] hover:bg-[#4A0A17]"
            >
              Return to the sanctuary
            </Link>
          </>
        )}

        {status === "timeout" && (
          <>
            <Flame className="w-10 h-10 text-[#E7B04A] mx-auto" strokeWidth={1.4} />
            <h1 className="mt-4 font-serif-display text-3xl text-[#FFFFFF]">
              Still confirming…
            </h1>
            <p className="mt-3 text-sm text-[#FBEBD3] font-scripture">
              Your payment is processing. If your card was charged, thank you — you may
              close this page.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-full gold-border bg-[#5C0E1D] px-5 py-2.5 text-sm text-[#FFFFFF] hover:bg-[#4A0A17]"
            >
              Return to the sanctuary
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancelPage = () => (
  <div className="min-h-screen flex items-center justify-center px-5 py-10">
    <div
      data-testid="payment-cancel-page"
      className="w-full max-w-lg rounded-2xl bg-[#5C0E1D] gold-border-strong p-8 sm:p-10 text-center fade-up"
    >
      <Flame
        className="w-10 h-10 text-[#E7B04A] mx-auto"
        strokeWidth={1.4}
        fill="rgba(231,176,74,0.15)"
      />
      <h1 className="mt-4 font-serif-display text-3xl text-[#FFFFFF]">
        No gift given — and that is okay.
      </h1>
      <p className="mt-3 text-sm text-[#FBEBD3] font-scripture italic">
        Ask God will always be free. You are welcome here whether you give or not.
      </p>
      <Link
        to="/"
        data-testid="cancel-return-home-link"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E7B04A] px-5 py-2.5 text-sm text-[#5C0E1D] hover:bg-[#F5C76B]"
      >
        Return to the sanctuary
      </Link>
    </div>
  </div>
);
